import React from 'react';
import { Box, Heading } from '@wix/design-system';
import { RecommendationLabel } from '@wix/ambassador-premium-domains-v1-recommendation/types';
import { parseDomain } from '@wix/domains-serverless-interface';
import { ComBrRestriction, DomainText, PricingSection, SearchRecommendationLayout, isInteractiveClickTarget } from '@wix/domains-search-suite';
import { useIsMobile } from '../../hooks/useIsMobile';
import { AvailableNowBadge } from './AvailableNowBadge';
import { MatchLabelBadge } from './MatchLabelBadge';
import { HeroCtaButton } from './HeroCtaButton';
import { HeroBusinessEmailExample } from './HeroBusinessEmailExample';
import { StackedMobileCard } from './StackedMobileCard';
import { HeroInsightBullets } from '../HeroInsightBullets';
import s from './HeroCard.css';
const SCALED_TITLE_MIN_FONT_SIZE = '1.25rem';
const SCALED_EXACT_MATCH_MAX_FONT_SIZE = '2rem';
export const CompactHeroCard = _ref => {
  let {
    recommendation,
    onClickGetIt,
    onItemClick,
    disabled = false,
    dataHook = 'hero-card',
    useCompactHeroCardLayout = false,
    stackedMobileLayout = false,
    Label,
    badgeLabel,
    ctaPriority = 'primary',
    businessEmailExample = false,
    isPricingLoading = false,
    compactPriceLabel = false,
    titleOverflowStrategy = 'ellipsis',
    PriceNote
  } = _ref;
  const isMobile = useIsMobile();
  const {
    domain = '',
    label,
    price
  } = recommendation;
  const {
    tld
  } = parseDomain(domain);
  const isExactMatch = label === RecommendationLabel.EXACT_MATCH;
  const matchBadgeLabel = isExactMatch ? label : badgeLabel;
  const showMatchBadge = isExactMatch || Boolean(badgeLabel);
  const showClickableCardStyling = !!onItemClick && !isMobile;
  const isStackedMobile = stackedMobileLayout && isMobile;
  const ctaButton = /*#__PURE__*/React.createElement(HeroCtaButton, {
    size: isStackedMobile ? 'large' : 'medium',
    priority: ctaPriority,
    disabled: disabled,
    fullWidth: isStackedMobile,
    onClick: () => onClickGetIt == null ? void 0 : onClickGetIt(recommendation),
    dataHook: `${dataHook}-cta`
  });
  const badgesRow = /*#__PURE__*/React.createElement(Box, {
    direction: "horizontal",
    gap: "SP2",
    verticalAlign: "middle",
    flexWrap: "wrap"
  }, showMatchBadge && matchBadgeLabel && (Label && matchBadgeLabel !== 'RECOMMENDED' ? /*#__PURE__*/React.createElement(Label, {
    primary: true,
    label: matchBadgeLabel,
    domain: domain,
    size: isMobile ? 'tiny' : 'small'
  }) : /*#__PURE__*/React.createElement(MatchLabelBadge, {
    label: matchBadgeLabel,
    optimized: !isExactMatch,
    primary: ctaPriority === 'primary'
  })), isExactMatch && /*#__PURE__*/React.createElement(AvailableNowBadge, null));
  const scalesTitleToFit = titleOverflowStrategy === 'scale';
  const scaledAlternativeMaxFontSize = isMobile && !isStackedMobile ? '1.875rem' : '1.75rem';
  const titleHeading = isExactMatch ? /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    className: scalesTitleToFit ? s.titleColumn : undefined
  }, /*#__PURE__*/React.createElement(SearchRecommendationLayout.TitleDisplay, {
    title: domain,
    exactMatch: true,
    useCompactTitleStyle: useCompactHeroCardLayout,
    showComBrRestriction: useCompactHeroCardLayout && tld === 'com.br',
    overflowStrategy: titleOverflowStrategy,
    minFontSize: SCALED_TITLE_MIN_FONT_SIZE,
    maxFontSize: SCALED_EXACT_MATCH_MAX_FONT_SIZE
  })) : /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "4px",
    className: scalesTitleToFit ? s.titleColumn : undefined
  }, /*#__PURE__*/React.createElement(DomainText, {
    overflowStrategy: titleOverflowStrategy,
    minFontSize: SCALED_TITLE_MIN_FONT_SIZE,
    maxFontSize: scaledAlternativeMaxFontSize,
    maxWidth: isMobile ? '310px' : '440px',
    textComponent: Heading,
    textComponentProps: {
      size: isMobile ? 'large' : 'extraLarge',
      as: 'h1'
    },
    className: scalesTitleToFit ? s.scaledCompactDomain : isStackedMobile ? s.stackedCompactDomain : isMobile ? s.compactDomainMobile : s.compactDomain,
    dataHook: `${dataHook}-title`
  }, domain), /*#__PURE__*/React.createElement(ComBrRestriction, {
    domainName: domain
  }));
  const pricingSection = /*#__PURE__*/React.createElement(PricingSection, {
    pricing: price,
    tld: tld,
    domainName: domain,
    useMobileLayout: true,
    compactPriceLabel: compactPriceLabel,
    useIntroPriceLabelCopy: compactPriceLabel && useCompactHeroCardLayout,
    denseStrikethroughStyle: useCompactHeroCardLayout,
    matchStrikethroughSize: isMobile && useCompactHeroCardLayout,
    isPricingLoading: isPricingLoading
  });
  const stackedMobileSection = /*#__PURE__*/React.createElement(StackedMobileCard, {
    spacious: isExactMatch,
    badges: badgesRow,
    title: /*#__PURE__*/React.createElement(Box, {
      direction: "vertical",
      gap: "4px",
      minWidth: 0
    }, titleHeading, businessEmailExample && /*#__PURE__*/React.createElement(HeroBusinessEmailExample, {
      domain: domain,
      size: "small"
    })),
    bullets: isExactMatch && /*#__PURE__*/React.createElement(HeroInsightBullets, {
      domain: domain,
      size: "small"
    }),
    pricing: pricingSection,
    cta: ctaButton
  });
  const compactSection = /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    padding: "SP6",
    gap: isMobile && useCompactHeroCardLayout ? '16px' : 'SP6',
    flex: 1
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: isMobile && useCompactHeroCardLayout ? '16px' : '8px',
    minWidth: 0
  }, badgesRow, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "4px",
    minWidth: 0
  }, titleHeading, businessEmailExample && /*#__PURE__*/React.createElement(HeroBusinessEmailExample, {
    domain: domain,
    size: "small"
  }), PriceNote && /*#__PURE__*/React.createElement(PriceNote, {
    price: price,
    domain: domain,
    size: "small"
  }))), /*#__PURE__*/React.createElement(Box, {
    direction: "horizontal",
    align: "space-between",
    verticalAlign: "bottom",
    gap: "SP4",
    flexWrap: "wrap",
    marginTop: "auto"
  }, pricingSection, /*#__PURE__*/React.createElement(Box, {
    flexShrink: 0,
    marginLeft: "auto"
  }, ctaButton)));
  const compactExactSection = /*#__PURE__*/React.createElement(Box, {
    direction: isMobile ? 'vertical' : 'horizontal',
    align: isMobile ? undefined : 'space-between',
    verticalAlign: isMobile ? undefined : 'middle',
    padding: isMobile ? '24px' : '36px 24px',
    gap: isMobile && useCompactHeroCardLayout ? '16px' : 'SP4'
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: isMobile && useCompactHeroCardLayout ? '16px' : useCompactHeroCardLayout ? '12px' : '8px',
    align: "left",
    flex: scalesTitleToFit && !isMobile ? 1 : undefined,
    minWidth: 0
  }, badgesRow, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "4px",
    minWidth: 0
  }, titleHeading, businessEmailExample && /*#__PURE__*/React.createElement(HeroBusinessEmailExample, {
    domain: domain,
    size: "small"
  }), PriceNote && /*#__PURE__*/React.createElement(PriceNote, {
    price: price,
    domain: domain,
    size: "small"
  }))), /*#__PURE__*/React.createElement(Box, {
    direction: "horizontal",
    verticalAlign: "middle",
    gap: "SP4",
    flexShrink: 0,
    width: isMobile ? '100%' : undefined,
    align: isMobile ? 'space-between' : undefined
  }, /*#__PURE__*/React.createElement(PricingSection, {
    pricing: price,
    tld: tld,
    domainName: domain,
    compactPriceLabel: compactPriceLabel,
    isPricingLoading: isPricingLoading
  }), ctaButton));
  const cardSection = isStackedMobile ? stackedMobileSection : isExactMatch ? compactExactSection : compactSection;
  return /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    width: !isExactMatch && !isMobile ? undefined : '100%',
    flex: !isExactMatch && !isMobile ? 1 : undefined,
    minWidth: !isExactMatch && !isMobile ? 0 : undefined,
    minHeight: !isExactMatch && !isMobile && PriceNote ? '230px' : undefined,
    borderRadius: "12px",
    overflow: "hidden",
    className: [useCompactHeroCardLayout ? s.card : s.cardShadow, showClickableCardStyling && s.clickableCard].filter(Boolean).join(' '),
    dataHook: dataHook
  }, onItemClick ? /*#__PURE__*/React.createElement("div", {
    className: s.cardClickArea,
    "data-hook": `${dataHook}-click-area`,
    onClick: event => {
      if (disabled || isInteractiveClickTarget(event.target)) {
        return;
      }
      onItemClick(recommendation);
    }
  }, cardSection) : cardSection);
};
//# sourceMappingURL=CompactHeroCard.js.map